import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import {
  LocalVideoTrack,
  RemoteUser,
  useJoin,
  useRTCClient,
  useRemoteUsers,
  useClientEvent,
} from "agora-rtc-react";
import AgoraRTC from "agora-rtc-sdk-ng";
import { configType } from "./config";

// MUI Components
import {
  Box,
  IconButton,
  Typography,
  Paper,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
  styled,
  useTheme,
  alpha,
  Button
} from "@mui/material";

// MUI Icons
import {
  Videocam as VideocamIcon,
  VideocamOff as VideocamOffIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  CallEnd as CallEndIcon,
  People as PeopleIcon,
  Add as AddIcon,
  Minimize as MinimizeIcon,
  Close as CloseIcon,
  DragIndicator as DragIndicatorIcon,
  FiberManualRecord as FiberManualRecordIcon,
  MoreVert as MoreVertIcon,
  ScreenShare as ScreenShareIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";

// ---------------- Agora Context (unchanged) ----------------
const AgoraContext = createContext(null);

export const AgoraProvider = ({ children, localCameraTrack, localMicrophoneTrack }) => (
  <AgoraContext.Provider value={{ localCameraTrack, localMicrophoneTrack }}>
    {children}
  </AgoraContext.Provider>
);

export const useAgoraContext = () => {
  const context = useContext(AgoraContext);
  if (!context) throw new Error("useAgoraContext must be used within an AgoraProvider");
  return context;
};

// Styled Components
const DraggablePaper = styled(Paper)(({ theme }) => ({
  position: "fixed",
  borderRadius: 16,
  overflow: "hidden",
  boxShadow: "0 20px 50px rgba(0,0,0,0.45)",
  display: "flex",
  flexDirection: "column",
  zIndex: 1000,
  background: theme.palette.background.default,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
}));

const Header = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(1, 2),
  background: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  cursor: "grab",
  "&:active": {
    cursor: "grabbing",
  },
}));

const VideoContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(2),
  gap: theme.spacing(2),
  overflow: "hidden",
  background: theme.palette.background.default,
}));

const ControlsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  background: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const ControlButton = styled(IconButton)(({ theme, active }) => ({
  backgroundColor: active ? theme.palette.primary.main : theme.palette.grey[800],
  color: "white",
  "&:hover": {
    backgroundColor: active ? theme.palette.primary.dark : theme.palette.grey[700],
  },
  width: 56,
  height: 56,
}));

const EndCallButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
  color: "white",
  "&:hover": {
    backgroundColor: theme.palette.error.dark,
  },
  width: 56,
  height: 56,
}));

const ResizeHandle = styled(Box)(({ theme }) => ({
  position: "absolute",
  right: 0,
  bottom: 0,
  width: 16,
  height: 16,
  cursor: "nwse-resize",
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "flex-end",
  "&::after": {
    content: '""',
    width: 8,
    height: 8,
    borderRight: `2px solid ${theme.palette.primary.main}`,
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    borderRadius: 2,
    opacity: 0.75,
  },
}));

const MinimizedContainer = styled(Paper)(({ theme }) => ({
  position: "fixed",
  right: 24,
  bottom: 24,
  zIndex: 1001,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  borderRadius: 28,
  background: theme.palette.background.paper,
  boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
  cursor: "pointer",
}));

// ---------------- Modern Video Call Modal ----------------
function ModernVideoCallModal({
  isOpen,
  onClose,
  children,
  title = "Call",
  // Button state and handlers
  micMuted,
  cameraOff,
  onToggleMic,
  onToggleCamera,
  onEndCall,
  onAddParticipants,
}) {
  const theme = useTheme();
  const [minimized, setMinimized] = useState(false);
  const [pos, setPos] = useState({ x: 80, y: 60 });
  const [size, setSize] = useState({ w: 1000, h: 640 });
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const dragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const modalRef = useRef(null);

  // Drag handlers
  const onMouseDownHeader = (e) => {
    if (minimized) return;
    dragging.current = true;
    dragStart.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const onMouseMove = (e) => {
    if (!dragging.current) return;
    setPos({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y,
    });
  };

  const onMouseUp = () => {
    dragging.current = false;
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
  };

  // Resize handle
  const resizing = useRef(false);
  const resizeStart = useRef({ x: 0, y: 0, w: 0, h: 0 });

  const onMouseDownResize = (e) => {
    if (minimized) return;
    resizing.current = true;
    resizeStart.current = { x: e.clientX, y: e.clientY, w: size.w, h: size.h };
    document.addEventListener("mousemove", onResizeMove);
    document.addEventListener("mouseup", onResizeUp);
    e.stopPropagation();
  };

  const onResizeMove = (e) => {
    if (!resizing.current) return;
    const dw = e.clientX - resizeStart.current.x;
    const dh = e.clientY - resizeStart.current.y;
    const newW = Math.max(520, resizeStart.current.w + dw);
    const newH = Math.max(360, resizeStart.current.h + dh);
    setSize({ w: newW, h: newH });
  };

  const onResizeUp = () => {
    resizing.current = false;
    document.removeEventListener("mousemove", onResizeMove);
    document.removeEventListener("mouseup", onResizeUp);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      {!minimized && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 999,
          }}
        />
      )}

      {/* Dock when minimized */}
      {minimized && (
  <DraggablePaper
    ref={modalRef}
    sx={{
      left: pos.x,
      top: pos.y,
      width: 300, // mini size
      height: 200,
    }}
  >
    <Header onMouseDown={onMouseDownHeader}>
      <Typography variant="subtitle2">{title} (Mini)</Typography>
      <Box sx={{ flexGrow: 1 }} />
      <Tooltip title="Restore">
        <IconButton size="small" onClick={() => setMinimized(false)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Header>

    <VideoContainer>
      {/* tiny video area */}
      <Box sx={{ flex: 1, position: "relative" }}>
        {children}
      </Box>
    </VideoContainer>

    <ControlsContainer sx={{ gap: 1, p: 1 }}>
      <ControlButton active={!cameraOff} onClick={onToggleCamera} sx={{ width: 40, height: 40 }}>
        {cameraOff ? <VideocamOffIcon fontSize="small" /> : <VideocamIcon fontSize="small" />}
      </ControlButton>
      <ControlButton active={!micMuted} onClick={onToggleMic} sx={{ width: 40, height: 40 }}>
        {micMuted ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
      </ControlButton>
      <EndCallButton onClick={onEndCall} sx={{ width: 40, height: 40 }}>
        <CallEndIcon fontSize="small" />
      </EndCallButton>
    </ControlsContainer>
  </DraggablePaper>
)}


      {/* Modal */}
      {!minimized && (
        <DraggablePaper
          ref={modalRef}
          sx={{
            left: pos.x,
            top: pos.y,
            width: size.w,
            height: size.h,
          }}
        >
          {/* Header */}
          <Header onMouseDown={onMouseDownHeader}>
            <DragIndicatorIcon sx={{ color: "text.secondary", mr: 1 }} />
            <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
              <FiberManualRecordIcon sx={{ color: theme.palette.primary.main, fontSize: 12, mr: 1 }} />
              <Typography variant="subtitle1" color="text.primary">
                {title}
              </Typography>
            </Box>

            {/* Window controls */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Tooltip title="Minimize">
                <IconButton size="small" onClick={() => setMinimized(true)}>
                  <MinimizeIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton size="small" onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Header>

          {/* Content */}
          <VideoContainer>
            {children}
          </VideoContainer>

          {/* Controls */}
          <ControlsContainer>
            <Tooltip title={cameraOff ? "Turn on camera" : "Turn off camera"}>
              <ControlButton 
                active={!cameraOff} 
                onClick={onToggleCamera}
              >
                {cameraOff ? <VideocamOffIcon /> : <VideocamIcon />}
              </ControlButton>
            </Tooltip>
            
            <Tooltip title={micMuted ? "Unmute microphone" : "Mute microphone"}>
              <ControlButton 
                active={!micMuted} 
                onClick={onToggleMic}
              >
                {micMuted ? <MicOffIcon /> : <MicIcon />}
              </ControlButton>
            </Tooltip>
            
            {/* <Tooltip title="Add Participants">
              <ControlButton onClick={() => setParticipantsOpen(true)}>
                <AddIcon />
              </ControlButton>
            </Tooltip> */}
            
            {/* <Tooltip title="Screen Share">
              <ControlButton>
                <ScreenShareIcon />
              </ControlButton>
            </Tooltip> */}
            
            {/* <Tooltip title="Settings">
              <ControlButton>
                <SettingsIcon />
              </ControlButton>
            </Tooltip> */}
            
            <Tooltip title="End Call">
              <EndCallButton onClick={onEndCall}>
                <CallEndIcon />
              </EndCallButton>
            </Tooltip>
          </ControlsContainer>

          {/* Resize handle */}
          <ResizeHandle onMouseDown={onMouseDownResize} />

          {/* Participants Dialog */}
          <Dialog open={participantsOpen} onClose={() => setParticipantsOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              <Box display="flex" alignItems="center">
                <PeopleIcon sx={{ mr: 1 }} />
                Participants
              </Box>
            </DialogTitle>
            <DialogContent>
              <List>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar>Y</Avatar>
                  </ListItemAvatar>
                  <ListItemText primary="You" secondary="Host" />
                  <Chip 
                    icon={micMuted ? <MicOffIcon /> : <MicIcon />} 
                    label={micMuted ? "Muted" : "Unmuted"} 
                    size="small" 
                    color={micMuted ? "default" : "primary"}
                    variant="outlined"
                  />
                </ListItem>
                <ListItem secondaryAction={
                  <IconButton edge="end">
                    <MoreVertIcon />
                  </IconButton>
                }>
                  <ListItemAvatar>
                    <Avatar>J</Avatar>
                  </ListItemAvatar>
                  <ListItemText primary="John Doe" />
                  <Chip 
                    icon={<MicIcon />} 
                    label="Unmuted" 
                    size="small" 
                    color="primary"
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                </ListItem>
                <ListItem secondaryAction={
                  <IconButton edge="end">
                    <MoreVertIcon />
                  </IconButton>
                }>
                  <ListItemAvatar>
                    <Avatar>J</Avatar>
                  </ListItemAvatar>
                  <ListItemText primary="Jane Smith" />
                  <Chip 
                    icon={<MicOffIcon />} 
                    label="Muted" 
                    size="small" 
                    color="default"
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                </ListItem>
              </List>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setParticipantsOpen(false)}>Close</Button>
              <Button variant="contained" startIcon={<AddIcon />} onClick={onAddParticipants}>
                Add Participant
              </Button>
            </DialogActions>
          </Dialog>
        </DraggablePaper>
      )}
    </>
  );
}

// ---------------- Your Agora Manager (with button functionality) ----------------
export const AgoraAudioManager = ({ config, client, children }) => {
  const theme = useTheme();
  const [localCameraTrack, setLocalCameraTrack] = useState(null);
  const [localMicrophoneTrack, setLocalMicrophoneTrack] = useState(null);
  const [joined, setJoined] = useState(false);
  const remoteUsers = useRemoteUsers();
  const [role, setRole] = useState("host");
  const [open, setOpen] = useState(true);
  const [micMuted, setMicMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);

  console.log(client);

  async function publishWithRetry(client, tracks, maxRetries = 3, delay = 2000) {
    const validTracks = tracks.filter(t => t && t.trackMediaType);
    if (validTracks.length === 0) {
      throw new Error("❌ No valid local tracks to publish.");
    }

    let attempt = 0;
    while (attempt < maxRetries) {
      try {
        console.log(`Publishing attempt ${attempt + 1}`);
        await client.publish(tracks);
        console.log("✅ Published successfully");
        return true;
      } catch (err) {
        console.warn("❌ Publish failed:", err);
        attempt++;
        if (attempt < maxRetries) {
          await new Promise(res => setTimeout(res, delay));
        } else {
          throw new Error("Max retries reached. Could not publish tracks.");
        }
      }
    }
  }


  useEffect(() => {
    return () => {
      if (client) {
        client.leave().catch(err => console.error("Leave failed:", err));
      }
    };
  }, [client]);
  

  // remote subscribe
  useEffect(() => {
    const handleUserPublished = async (user, mediaType) => {
      await client.subscribe(user, mediaType);
      console.log("Subscribed to", user.uid, mediaType);
    };
    const handleUserUnpublished = (user) => {
      console.log("User unpublished:", user.uid);
    };
    client.on("user-published", handleUserPublished);
    client.on("user-unpublished", handleUserUnpublished);
    return () => {
      client.off("user-published", handleUserPublished);
      client.off("user-unpublished", handleUserUnpublished);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // join (unchanged)
  const { data: uid, isConnected, isLoading, error } = useJoin({
    appid: config.appId,
    channel: config.channelName,
    token: config.rtcToken,
    uid: config.uid,
  });

  // local tracks create + publish (unchanged)
  useEffect(() => {
    if (!isConnected) {
      return;
    }

    let camera, mic;
    const initLocalTracks = async () => {
      try {
        [mic, camera] = await AgoraRTC.createMicrophoneAndCameraTracks();
        setLocalCameraTrack(camera);
        setLocalMicrophoneTrack(mic);
        await publishWithRetry(client, [mic, camera]);
      } catch (err) {
        console.error("Failed to init local tracks:", err);
      }
    };
    initLocalTracks();
    return () => {
      mic?.close();
      camera?.close();
      client.unpublish([mic, camera]).catch(() => { });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);



  useClientEvent(client, "user-joined", (user) => {
    console.log("The user", user.uid, "has joined the channel");
  });
  useClientEvent(client, "user-left", (user) => {
    console.log("The user", user.uid, "has left the channel");
  });
  useClientEvent(client, "user-published", (user, mediaType) => {
    console.log("The user", user.uid, "has published media in the channel");
  });

  localMicrophoneTrack && localMicrophoneTrack.on("track-ended", async () => {
    const newMic = await AgoraRTC.createMicrophoneAudioTrack();
    await publishWithRetry(client, [newMic]);
  });

  const handleRoleChange = (event) => {
    setRole(event.target.value);
    const nextRole = event.target.value === "host" ? "host" : "audience";
    client
      .setClientRole(nextRole)
      .then(() => console.log("Client role set to", nextRole))
      .catch((error) => console.error("Error setting client role:", error));
  };

  const toggleMic = () => {
    if (localMicrophoneTrack) {
      if (micMuted) {

        // client.muteLocalAudioStream(false);
        localMicrophoneTrack.setEnabled(true);
      } else {
        localMicrophoneTrack.setEnabled(false);
        // client.muteLocalAudioStream(true);
      }
      setMicMuted(!micMuted);
    }
  };

  const toggleCamera = () => {
    if (localCameraTrack) {
      if (cameraOff){
        // client.muteLocalVideostream(true);
        localCameraTrack.setEnabled(true);
      } else {
        // client.muteLocalVideostream(false);
        localCameraTrack.setEnabled(false);
      }
      setCameraOff(!cameraOff);
    }
  };

  const endCall = () => {
    // Leave the channel
    client.leave();
    
    // Close local tracks
    if (localCameraTrack) {
      localCameraTrack.close();
    }
    if (localMicrophoneTrack) {
      localMicrophoneTrack.close();
    }
    
    // Close the modal
    setOpen(false);
  };

  const addParticipants = () => {
    // In a real app, this would open a dialog to invite participants
    // For demo purposes, we'll just show an alert
    alert("This would open a dialog to add participants in a real application");
  };

  return (
    <ModernVideoCallModal
      isOpen={open}
      onClose={() => setOpen(false)}
      title="Connect Health Pro"
      // Pass button state and handlers
      micMuted={micMuted}
      cameraOff={cameraOff}
      onToggleMic={toggleMic}
      onToggleCamera={toggleCamera}
      onEndCall={endCall}
      onAddParticipants={addParticipants}
    >
      {/* Role selector */}
      {config.selectedProduct === "live" && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
          <Chip 
            label="Host" 
            variant={role === "host" ? "filled" : "outlined"}
            onClick={() => handleRoleChange({ target: { value: "host" } })}
            color="primary"
          />
          <Chip 
            label="Audience" 
            variant={role === "audience" ? "filled" : "outlined"}
            onClick={() => handleRoleChange({ target: { value: "audience" } })}
            color="primary"
          />
        </Box>
      )}

      {/* Videos area */}
      <Box sx={{ 
        display: "grid", 
        gridTemplateColumns: "1fr 1fr", 
        gap: 2, 
        width: "100%", 
        height: "100%",
        overflow: "hidden"
      }}>
        {/* Local video */}
        <Paper 
          sx={{ 
            height: "100%", 
            minHeight: 280, 
            width: "100%", 
            backgroundColor: "background.paper",
            borderRadius: 2,
            overflow: "hidden",
            position: "relative",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {localCameraTrack ? (
            <>
              <LocalVideoTrack track={localCameraTrack} play={false} />
              {cameraOff && (
                <Box sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0,0,0,0.7)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                  <VideocamOffIcon sx={{ fontSize: 48, color: "grey.500" }} />
                </Box>
              )}
            </>
          ) : (
            <Box sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "text.secondary",
              flexDirection: "column",
              gap: 1
            }}>
              <VideocamOffIcon sx={{ fontSize: 48 }} />
              <Typography>Initializing camera…</Typography>
            </Box>
          )}
          
          {/* User info overlay */}
          <Box sx={{
            position: "absolute",
            bottom: 8,
            left: 8,
            backgroundColor: "rgba(0,0,0,0.6)",
            borderRadius: 2,
            padding: 1,
            display: "flex",
            alignItems: "center",
            gap: 1
          }}>
            <Avatar sx={{ width: 24, height: 24 }}>Y</Avatar>
            <Typography variant="body2" color="white">
              You {micMuted && <MicOffIcon sx={{ fontSize: 14 }} />}
            </Typography>
          </Box>
        </Paper>

        {/* Remote videos */}
        <Box sx={{ 
          display: "flex", 
          flexDirection: "column",
          gap: 2,
          height: "100%",
          overflow: "auto",
          pr: 0.5
        }}>
          {remoteUsers?.length === 0 && (
            <Paper sx={{ 
              height: 280, 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center",
              backgroundColor: "background.paper",
              borderRadius: 2,
              border: `1px dashed ${theme.palette.divider}`,
              flexDirection: "column",
              gap: 1
            }}>
              <PeopleIcon sx={{ fontSize: 48, color: "text.secondary" }} />
              <Typography color="text.secondary">
                Waiting for participants…
              </Typography>
            </Paper>
          )}

          {remoteUsers?.map((remoteUser) => (
            <Paper 
              key={remoteUser.uid}
              sx={{ 
                height: 280, 
                width: "100%", 
                backgroundColor: "background.paper",
                borderRadius: 2,
                overflow: "hidden",
                position: "relative",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <RemoteUser user={remoteUser} playVideo={true} playAudio={true} />
              
              {/* User info overlay */}
              <Box sx={{
                position: "absolute",
                bottom: 8,
                left: 8,
                backgroundColor: "rgba(0,0,0,0.6)",
                borderRadius: 2,
                padding: 1,
                display: "flex",
                alignItems: "center",
                gap: 1
              }}>
                <Avatar sx={{ width: 24, height: 24 }}>
                  {String.fromCharCode(65 + (remoteUser.uid % 26))}
                </Avatar>
                <Typography variant="body2" color="white">
                  User {remoteUser.uid}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    </ModernVideoCallModal>
  );
};

// Export the AgoraAudioManager component as the default export
export default AgoraAudioManager;